import styled from '@emotion/styled'
import { PostInviteUserBodyDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledCheckboxes,
  ControlledInput,
  emailInputPattern,
  ExtranetContainer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacCheckbox,
  PharmacColumn,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacLabel,
  PharmacText,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Control, FieldValues, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useUserCustomerDetails } from '../../features/customer/useUserCustomerDetails'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'
import { __CUSTOMER_INVITE_LINKS_QUERY_KEY } from '../../views/customer/CustomerInviteLinksView'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'

const InfoFormContainer = styled(PharmacContainer)`
  background-color: var(--color-accent-2);
  border-radius: 20px;
`

const BasicUserCheckbox = styled(PharmacCheckbox)`
  background-color: var(--color-accent-2);
  width: 1;

  &:hover {
    cursor: not-allowed;
  }
`

type Props = {
  setShow: () => void
}

export const AddCustomerUser = ({ setShow }: Props) => {
  const { t } = useLanguage(['extranet', 'common'])
  const { minDesktop, maxMobile } = useBreakpoint()
  const { allRoles } = useUserCustomerDetails()
  const [addedUserModal, setAddedUserModal] = useState<boolean>(false)
  const { customer } = useCustomerContext()
  const { isPharmacCustomerService } = useAuthenticationContext()
  const navigate = useNavigate()
  const createLink = useExtranetPath()

  const filteredRolesList = (allRoles || [])
    .filter(({ role }) => {
      if (role == 'customer-user') return false
      if (role === 'customer-view-orders' && customer?.companyRole?.role !== 'A01') return false
      return true
    })
    .map((role) => ({
      value: role.role,
      name: {
        fi: t(`customer.roles.${role.role}`),
        sv: t(`customer.roles.${role.role}`),
        en: t(`customer.roles.${role.role}`),
      },
    }))

  const formhook = useForm<PostInviteUserBodyDto>({
    mode: 'onChange',
    defaultValues: {
      userEmail: '',
      roles: ['customer-user'],
    },
  })

  const queryClient = useQueryClient()

  const { mutate: addCustomerUser } = useMutation({
    mutationFn: (invite: { id: number; data: PostInviteUserBodyDto }) =>
      api.customers.postInviteCustomerUser(invite.id, invite.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [__CUSTOMER_INVITE_LINKS_QUERY_KEY],
      })
    },
  })

  const handleAddCustomerUser = (data: PostInviteUserBodyDto) => {
    addCustomerUser({ id: Number(customer?.id), data })
  }
  const handleSendLink = (data: PostInviteUserBodyDto) => {
    handleAddCustomerUser(data)
    setAddedUserModal(true)
  }

  return (
    <InfoFormContainer>
      <PharmacFieldset
        marginLarge={!maxMobile}
        marginSmallTop={maxMobile}
        marginLargeBottom={maxMobile}
      >
        <PharmacFieldsetLegend>
          <PharmacH5>{t('customer.newUser')}</PharmacH5>
        </PharmacFieldsetLegend>
        <ExtranetContainer
          paddingMediumVertical
          paddingLargeLeft={!maxMobile}
          half={minDesktop}
        >
          <PharmacContainer style={{ width: '100%' }}>
            <ControlledInput
              name="userEmail"
              rules={{ required: true, pattern: emailInputPattern }}
              requiredError={t('common:requiredFieldError')}
              patternError={t('common:emailPatternError')}
              control={formhook.control as unknown as Control<FieldValues>}
              label={t('emailAddress', { ns: 'common' })}
            />
          </PharmacContainer>
        </ExtranetContainer>
        <ExtranetContainer
          paddingLargeTop
          paddingLargeLeft={!maxMobile}
        >
          <PharmacColumn>
            <PharmacContainer marginLargeBottom={!maxMobile}>
              <PharmacColumn>
                <PharmacLabel marginSmallBottom>
                  {t('user.userRoles', { ns: 'extranet' })}
                </PharmacLabel>
                <BasicUserCheckbox
                  marginSmallVertical
                  checked
                  disabled
                  handleChange={() => null}
                >
                  {t('customer.roles.customer-user', { ns: 'extranet' })}
                </BasicUserCheckbox>
                <ControlledCheckboxes
                  column
                  data={filteredRolesList}
                  name="roles"
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </PharmacColumn>
            </PharmacContainer>
          </PharmacColumn>
        </ExtranetContainer>
      </PharmacFieldset>

      <ExtranetContainer
        paddingLargeRight={minDesktop}
        full
        justifyEnd={minDesktop}
        justifyCenter={!minDesktop}
      >
        <PharmacButton
          fullWidth={!minDesktop}
          variant="secondary"
          marginLargeRight
          onClick={setShow}
        >
          {t('cancel', { ns: 'common' })}
        </PharmacButton>
        <PharmacButton
          fullWidth={!minDesktop}
          onClick={formhook.handleSubmit(handleSendLink)}
          disabled={!formhook.formState.isValid || isPharmacCustomerService}
        >
          {t('customer.sendInviteLink')}
        </PharmacButton>
      </ExtranetContainer>
      <ConfirmationModal
        preventClose
        showOnProp={addedUserModal}
        title={t('customer.newUserModalTitle')}
        onContinue={() => {
          setAddedUserModal(false)
          navigate(createLink('oma-annos.customership.inviteLinks'))
        }}
        content={
          <ExtranetContainer
            full
            flexColumn
            paddingSmallTop
          >
            <ExtranetContainer
              full
              flexColumn={!minDesktop}
            >
              <PharmacText
                style={{
                  paddingRight: 'var(--spacing-tiny)',
                  paddingBottom: 'var(--spacing-tiny)',
                }}
              >
                {t('customer.newUserModalDescription')}
              </PharmacText>
              <PharmacText weight="semibold">{formhook.getValues().userEmail}</PharmacText>
            </ExtranetContainer>
            <PharmacText paddingMediumTop>{t('customer.newInviteLinkExpiresIn')}</PharmacText>
          </ExtranetContainer>
        }
      />
    </InfoFormContainer>
  )
}
